import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { catchError, from, map, Observable, of, switchMap } from 'rxjs';
import { isSessionExpiredError, UserService } from '../user/user.service';
import { TenantAccessLostError } from '../tenant/tenant-access-lost.error';
import { TenantService } from '../tenant/tenant.service';
import { ToastService } from '../toast/toast.service';

type AuthGuardResult =
  | boolean
  | UrlTree
  | Observable<boolean | UrlTree>
  | Promise<boolean | UrlTree>;

@Injectable({
  providedIn: 'root',
})
export class AuthGuardService {
  private router = inject(Router);
  private userService = inject(UserService);
  private tenantService = inject(TenantService);
  private toastService = inject(ToastService);

  public canActivate(state: RouterStateSnapshot): AuthGuardResult {
    return from(this.userService.isAuthenticated()).pipe(
      map((isAuthenticated) => {
        if (isAuthenticated) {
          return true;
        }
        return this.handleAuthenticationFailed(state);
      })
    );
  }

  public canActivateLogin(route: ActivatedRouteSnapshot): AuthGuardResult {
    return from(this.userService.isAuthenticated()).pipe(
      map((isAuthenticated) => {
        if (!isAuthenticated) {
          // Not logged in. Login page can show up.
          return true;
        }

        // Already logged in.
        // Prevent login page to show up
        // Redirect to the return URL.
        const dest = route.queryParams.return || '/';
        console.log('Already logged in. Redirecting to ' + dest);
        return this.router.parseUrl(dest);
      })
    );
  }

  public canActivateWithOrganization(
    state: RouterStateSnapshot
  ): AuthGuardResult {
    return from(this.userService.isAuthenticated()).pipe(
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) {
          return of(this.handleAuthenticationFailed(state));
        }
        return this.autoSelectSingleTenant(state).pipe(
          map((redirect) => redirect ?? this.checkTenantSelection(state))
        );
      })
    );
  }

  /**
   * Auto-selects the only tenant of a single-tenant user and waits until the
   * session is re-scoped to it, so no tenant-scoped request can go out with a
   * tenant-less token.
   * @returns A redirect if the re-scoping failed, otherwise null.
   */
  private autoSelectSingleTenant(
    state: RouterStateSnapshot
  ): Observable<UrlTree | null> {
    return this.userService.autoSelectSingleTenant().pipe(
      map((): UrlTree | null => null),
      catchError((error) => of(this.handleAutoSelectFailed(error, state)))
    );
  }

  private handleAutoSelectFailed(
    error: unknown,
    state: RouterStateSnapshot
  ): UrlTree {
    if (error instanceof TenantAccessLostError) {
      // tenant-login said 403: the only tenant is gone. UserService has already
      // cleared the selection, refreshed the org list, informed the user and
      // navigated; no second toast, no logout. Plain org-selection redirect (no
      // auto-select again, so no loop). Mostly a fallback: handleAccessLost's
      // own navigation normally supersedes this guard navigation, so this tree
      // only matters if that navigation fails.
      return this.createOrgSelectionUrlTree(state);
    }

    if (isSessionExpiredError(error)) {
      // No refresh token, or tenant-login rejected with 401 (refresh token
      // invalid/revoked): not recoverable without a fresh login, same as
      // AuthInterceptor.
      this.userService.invalidateSession();
      return this.handleAuthenticationFailed(state);
    }

    // The user service already reverted the tenant selection.
    console.error('Could not scope the session to the only tenant.', error);
    void this.toastService.displayErrorToast(
      'Deine Organisation konnte nicht automatisch ausgewählt werden. Bitte versuche es erneut.'
    );
    return this.createOrgSelectionUrlTree(state);
  }

  private checkTenantSelection(state: RouterStateSnapshot): boolean | UrlTree {
    if (!this.tenantService.hasTenantSelected()) {
      console.log('No organization selected. Redirecting to org-selection.');
      return this.createOrgSelectionUrlTree(state);
    }

    // Tenant-login-admin: super admin logged into a tenant they don't belong to
    // Skip tenant validity check since the tenant won't be in their session tenants
    if (this.userService.isTenantLoginAdmin()) {
      return true;
    }

    // Validate that the selected tenant is still valid
    if (
      !this.tenantService.isSelectedTenantValid(
        this.userService.getTenantsFromSession()
      )
    ) {
      console.log(
        'Selected organization is no longer valid. Redirecting to org-selection.'
      );
      this.tenantService.clearSelectedTenantId();
      return this.createOrgSelectionUrlTree(state);
    }

    return true;
  }

  private createOrgSelectionUrlTree(state: RouterStateSnapshot): UrlTree {
    return this.router.createUrlTree(['/org-selection'], {
      queryParams: { return: state.url },
    });
  }

  private handleAuthenticationFailed(state: RouterStateSnapshot): UrlTree {
    console.log(`No user logged in. Redirecting to login.`);
    return this.router.createUrlTree(['/login'], {
      queryParams: { return: state.url },
    });
  }
}
